import React, {
  createContext,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CommandPaletteHistoryEntry, CommandPaletteTrigger } from './types';
import CommandPalette, { CommandPaletteRef } from './CommandPalette';
import useKeydown from '../../hooks/useKeydown';

interface AppCommandPaletteConfigLayer<C extends {}> {
  id: string;
  enabled: boolean;
  initialLocation?: CommandPaletteHistoryEntry<C>;
  priority: number;
  eventKey?: string;
}

interface AppCommandPaletteContextShape {
  pushConfigLayer: (layer: AppCommandPaletteConfigLayer<any>) => void;
  removeConfigLayer: (id: string) => void;
  open: () => void;
  openAt: CommandPaletteTrigger;
}

export const AppCommandPaletteContext =
  createContext<AppCommandPaletteContextShape | null>(null);

interface AppCommandPaletteProviderProps {
  eventKey?: string;
  children: React.ReactNode;
}

export function AppCommandPaletteProvider({
  // we use 'k' as the default, but for things like testing in admin, we might want to use 'j'
  eventKey = 'k',
  children,
}: AppCommandPaletteProviderProps) {
  const commandPaletteRef = useRef<CommandPaletteRef>(null);

  const [configLayers, setConfigLayers] = useState<
    Array<AppCommandPaletteConfigLayer<any>>
  >([]);

  // Current layer is the highest priority enabled layer, using natural order
  // as a tiebreaker (favour latest)
  const currentLayer = useMemo(() => {
    return configLayers
      .filter((layer) => layer.enabled)
      .sort((a, b) => {
        if (a.priority === b.priority) {
          return configLayers.indexOf(b) - configLayers.indexOf(a);
        }

        return b.priority - a.priority;
      })[0];
  }, [configLayers]);

  // Only use provider's eventKey if current layer doesn't have a custom eventKey
  const shouldUseProviderEventKey = !currentLayer?.eventKey;

  useKeydown((event: KeyboardEvent) => {
    if (
      shouldUseProviderEventKey &&
      event.key === eventKey &&
      (event.metaKey || event.ctrlKey)
    ) {
      event.preventDefault();
      commandPaletteRef.current?.toggle();
    }
  }, shouldUseProviderEventKey);

  const contextValue = useMemo(() => {
    return {
      pushConfigLayer: (layer: AppCommandPaletteConfigLayer<any>) =>
        setConfigLayers((layers) => [...layers, layer]),
      removeConfigLayer: (id: string) =>
        setConfigLayers((layers) => layers.filter((layer) => layer.id !== id)),
      open: () => commandPaletteRef.current?.open(),
      openAt: (location, options) =>
        commandPaletteRef.current?.openAt(location, options),
    } satisfies AppCommandPaletteContextShape;
  }, []);

  const initialLocation = currentLayer?.initialLocation;

  return (
    <>
      {initialLocation ? (
        <CommandPalette
          ref={commandPaletteRef}
          initialNavigationState={[initialLocation]}
        />
      ) : null}
      <AppCommandPaletteContext.Provider value={contextValue}>
        {children}
      </AppCommandPaletteContext.Provider>
    </>
  );
}

export function useAppCommandPalette<C extends {}>({
  enabled = true,
  initialLocation,
  priority = 1,
  eventKey = 'k',
}: {
  enabled: boolean;
  initialLocation: CommandPaletteHistoryEntry<C>;
  priority?: number;
  eventKey?: string;
}) {
  const layerId = useId();
  const context = useContext(AppCommandPaletteContext);

  if (!context) {
    throw new Error(
      'useAppCommandPalette must be used within AppCommandPaletteContext'
    );
  }

  // Only set up custom keyboard listener if eventKey is different from default
  const hasCustomEventKey = eventKey !== 'k';

  useKeydown((event: KeyboardEvent) => {
    if (event.key === eventKey && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      context.open();
    }
  }, enabled && hasCustomEventKey);

  useEffect(() => {
    context.pushConfigLayer({
      id: layerId,
      enabled,
      initialLocation,
      priority,
      eventKey: hasCustomEventKey ? eventKey : undefined,
    });

    return () => context.removeConfigLayer(layerId);
  }, [
    layerId,
    context,
    enabled,
    initialLocation,
    priority,
    eventKey,
    hasCustomEventKey,
  ]);

  return context;
}

export function useCommandPaletteTrigger(): CommandPaletteTrigger {
  const context = useContext(AppCommandPaletteContext);

  if (!context) {
    throw new Error(
      'useCommandPaletteTrigger must be used within AppCommandPaletteContext'
    );
  }

  return context.openAt;
}
